import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { getMessageForCode } from '../shared/errors/getErrorMessage';
import './ComponentLogin.css';

// Página pública (sin sesión) a la que llega el link de ForgotPasswordForm.jsx/ForgotPassword.js
// -- completa el flujo de recuperación: acá es donde de verdad se elige y aplica la contraseña
// nueva, nunca antes (ver la nota de diseño en ForgotPassword.js sobre por qué la sola
// solicitud ya no cambia nada).
export default function ResetPasswordPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [done, setDone] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 8) {
            setError(t('auth.resetPasswordTooShort'));
            return;
        }
        if (newPassword !== confirmPassword) {
            setError(t('auth.resetPasswordMismatch'));
            return;
        }

        setIsLoading(true);
        try {
            const res = await fetch('/api/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword }),
            });
            const data = await res.json();
            if (data.success) {
                setDone(true);
            } else {
                setError(getMessageForCode(data.code));
            }
        } catch {
            setError(t('auth.resetPasswordError'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0d0d0d' }}>
            <div style={{ position: 'relative', width: '100%', maxWidth: '377px' }}>
                <div className="component-login-container" style={{ margin: 0, maxWidth: '100%' }}>
                    <h2 className="login-title">{t('auth.resetPasswordTitle')}</h2>

                    {!token && (
                        <p className="login-subtitle">{t('auth.resetPasswordNoToken')}</p>
                    )}

                    {token && done && (
                        <>
                            <p className="login-subtitle">{t('auth.resetPasswordSuccess')}</p>
                            <button type="button" className="certify-btn" onClick={() => navigate('/')} style={{ width: '100%' }}>
                                {t('auth.backToLogin')}
                            </button>
                        </>
                    )}

                    {token && !done && (
                        <form onSubmit={handleSubmit}>
                            <p className="login-subtitle">{t('auth.resetPasswordSubtitle')}</p>

                            {error && (
                                <div style={{ color: '#ef4444', fontSize: '0.875rem', textAlign: 'center', marginBottom: '1rem' }}>
                                    {error}
                                </div>
                            )}

                            <div className="input-group" style={{ position: 'relative' }}>
                                <label className="input-label">{t('auth.newPasswordLabel')}</label>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="input-field"
                                    placeholder={t('auth.resetPasswordNewPasswordPlaceholder')}
                                    style={{ paddingRight: '40px' }}
                                    autoComplete="new-password"
                                />
                                <span
                                    onClick={() => setShowPassword((v) => !v)}
                                    style={{ position: 'absolute', right: '12px', top: '38px', cursor: 'pointer', color: 'var(--text-secondary)' }}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </span>
                            </div>

                            <div className="input-group">
                                <label className="input-label">{t('auth.confirmPasswordLabel')}</label>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="input-field"
                                    placeholder={t('auth.confirmPasswordPlaceholder')}
                                    autoComplete="new-password"
                                />
                            </div>

                            <button type="submit" className="certify-btn" disabled={isLoading} style={{ width: '100%', marginTop: '9px' }}>
                                {isLoading ? t('auth.forgotPasswordSending') : t('auth.resetPasswordSubmit')}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
